import { useVersionChangeCell } from "../../context/VersionChangesContext";
import ShiftCode from "@/components/shift/ShiftCode";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import {
  forwardRef,
  type ButtonHTMLAttributes,
  type MouseEventHandler,
  type ReactNode,
} from "react";

export type ShiftCodeStatusIndicator = {
  reason: string;
  label: string;
  title: string;
  rangePosition?: "single" | "start" | "middle" | "end";
};

export type ShiftCodeCellMode =
  | "weekly_locked"
  | "emergency_locked"
  | "assignment_locked"
  | "violation_messages"
  | "comment_popover"
  | "banned_wanted_violation"
  | "wanted_match"
  | "wanted_mismatch"
  | "default";

export type ShiftCodeCellProps = {
  cellUiMode: ShiftCodeCellMode;
  displayShiftId: string;
  fontSize: string;
  nurseId: string;
  day: number;
  dataShiftId: string;
  dataDefaultShift: string;
  dataShiftGb: string;
  /** 기본(default) 분기에서는 전달하지 않음 → `data-is-error` 생략 */
  dataIsError?: boolean;
  shiftBgColor: string;
  statusIndicator?: ShiftCodeStatusIndicator | null;

  onWeeklyLockedClick: MouseEventHandler<HTMLButtonElement>;
  onEmergencyLockedClick: MouseEventHandler<HTMLButtonElement>;
  onShiftCellClickEditOrFocus: MouseEventHandler<HTMLButtonElement>;
  onMouseEnterCell: MouseEventHandler<HTMLButtonElement>;
  onMouseLeaveCell: MouseEventHandler<HTMLButtonElement>;

  displayedViolationMessages: string[];

  comment?: string;
  bannedMessage?: string;
  bannedMessageLabel?: string;
  bannedShiftIds?: readonly string[];

  isWantedMatch?: boolean;
  isWantedMismatch?: boolean;
  wantedShiftId?: string;
};

type ShiftCodeDomButtonOwnProps = {
  displayShiftId: string;
  fontSize: string;
  nurseId: string;
  day: number;
  dataShiftId: string;
  dataDefaultShift: string;
  dataShiftGb: string;
  dataIsError?: boolean;
  shiftBgColor: string;
  statusIndicator?: ShiftCodeStatusIndicator | null;
  isWantedMatch?: boolean;
  children?: ReactNode;
};

type ShiftCodeDomButtonProps = ShiftCodeDomButtonOwnProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type" | "title">;

/**
 * `TooltipTrigger` / `PopoverTrigger`의 `asChild`는 ref 병합이 필요함.
 * Radix 툴팁은 열림에 `onPointerMove` / `onPointerLeave` 등을 쓰므로,
 * `onMouseEnter`만 넘기면 안 되고 Trigger가 합쳐 준 나머지 props를 `...rest`로 버튼에 그대로 전달해야 함.
 */
const ShiftCodeDomButton = forwardRef<
  HTMLButtonElement,
  ShiftCodeDomButtonProps
>(function ShiftCodeDomButton(
  {
    className,
    style,
    displayShiftId,
    fontSize,
    nurseId,
    day,
    dataShiftId,
    dataDefaultShift,
    dataShiftGb,
    dataIsError,
    shiftBgColor,
    statusIndicator,
    isWantedMatch = false,
    children,
    ...rest
  },
  ref
) {
  const versionCell = useVersionChangeCell(nurseId, day);
  const isResignationIndicator = statusIndicator?.reason === "퇴사";
  const showStatusLabel = statusIndicator && !isResignationIndicator;

  return (
    <button
      ref={ref}
      type="button"
      className={cn("", className)}
      data-nurse-id={nurseId}
      data-day={day}
      data-shift-id={dataShiftId}
      data-default-shift={dataDefaultShift}
      data-shift-gb={dataShiftGb}
      title={
        [
          statusIndicator?.title,
          versionCell
            ? `${versionCell.nurse_name || nurseId} · ${versionCell.date} · ${versionCell.before?.shift_id || "빈 칸"} → ${versionCell.after?.shift_id || "빈 칸"}`
            : undefined,
        ]
          .filter(Boolean)
          .join(" · ") || undefined
      }
      {...(statusIndicator
        ? { "data-cell-status-reason": statusIndicator.reason }
        : {})}
      {...(dataIsError !== undefined
        ? { "data-is-error": dataIsError ? "true" : "false" }
        : {})}
      style={{ fontSize, ...style }}
      {...rest}
    >
      <ShiftCode code={displayShiftId} color={shiftBgColor}>
        {statusIndicator && (
          <>
            {!isResignationIndicator && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 rounded-[5px] ring-2 ring-rose-500 ring-offset-1 ring-offset-white"
                data-cell-status-ring={statusIndicator.reason}
              />
            )}
            {showStatusLabel && (
              <span
                aria-hidden="true"
                className={cn(
                  "pointer-events-none absolute -right-1 -top-1 z-[2] flex items-center justify-center text-[9px] font-bold leading-none shadow-sm",
                  isResignationIndicator
                    ? "h-4 min-w-[24px] rounded-[4px] border border-amber-300 bg-amber-50 px-1 text-amber-800"
                    : "h-3.5 min-w-3.5 rounded-full bg-rose-600 px-0.5 text-white"
                )}
                data-cell-status-reason={statusIndicator.reason}
              >
                {statusIndicator.label}
              </span>
            )}
          </>
        )}
        {isWantedMatch && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute -right-1 -top-1 z-[3] flex size-3.5 items-center justify-center rounded-full bg-blue-400 text-white"
            data-wanted-match-badge="true"
          >
            <Check className="size-2.5" strokeWidth={3} />
          </span>
        )}
      </ShiftCode>
      {children}
    </button>
  );
});

function WantedViolationTooltipContent({
  displayShiftId,
  isWantedMismatch,
  bannedShiftIds,
  wantedShiftId,
}: {
  readonly displayShiftId: string;
  readonly isWantedMismatch: boolean;
  readonly bannedShiftIds: readonly string[] | undefined;
  readonly wantedShiftId: string | undefined;
}) {
  const hasBannedShiftViolation = Boolean(bannedShiftIds?.length);

  return (
    <TooltipContent
      className="w-max min-w-48 max-w-64 whitespace-normal border border-slate-200 bg-white px-3 py-2 text-slate-900 shadow-lg"
      side="top"
      sideOffset={7}
      arrowClassName="fill-white stroke-slate-200"
    >
      <div className="space-y-2">
        <p className="text-sm font-semibold leading-5 text-slate-900">
          원티드 조건 확인 필요
        </p>
        {isWantedMismatch && (
          <div className="space-y-0.5 text-xs leading-5">
            <p className="font-semibold text-rose-700">희망 근무 미일치</p>
            <p>희망: {wantedShiftId}</p>
            <p>현재: {displayShiftId}</p>
          </div>
        )}
        {hasBannedShiftViolation && (
          <div
            className={cn(
              "space-y-0.5 text-xs leading-5",
              isWantedMismatch && "border-t border-slate-100 pt-2"
            )}
          >
            <p className="font-semibold text-rose-700">금지 근무 위반</p>
            <p>현재: {displayShiftId}</p>
            <p>금지: {bannedShiftIds?.join(", ")}</p>
          </div>
        )}
      </div>
    </TooltipContent>
  );
}

/**
 * 근무표 셀 중앙 근무코드 + Tooltip/Popover 분기.
 * 트리거는 `ShiftCodeDomButton`(forwardRef → 실제 `<button>`)로 `asChild`와 호환.
 */
export function ShiftCodeCell(props: ShiftCodeCellProps) {
  const {
    cellUiMode,
    displayShiftId,
    fontSize,
    nurseId,
    day,
    dataShiftId,
    dataDefaultShift,
    dataShiftGb,
    dataIsError,
    shiftBgColor,
    statusIndicator,
    onWeeklyLockedClick,
    onEmergencyLockedClick,
    onShiftCellClickEditOrFocus,
    onMouseEnterCell,
    onMouseLeaveCell,
    displayedViolationMessages,
    comment,
    bannedMessage,
    bannedMessageLabel,
    bannedShiftIds,
    isWantedMatch = false,
    isWantedMismatch = false,
    wantedShiftId,
  } = props;

  const normalizedComment = comment?.trim();
  const normalizedBannedMessage = bannedMessage?.trim();
  const messageItems = [
    ...(normalizedComment
      ? [{ label: "원티드 신청 메시지", content: normalizedComment }]
      : []),
    ...(normalizedBannedMessage
      ? [
          {
            label: bannedMessageLabel ?? "금지 근무 메시지",
            content: normalizedBannedMessage,
          },
        ]
      : []),
  ];
  const messageAriaLabel = [
    ...(isWantedMatch ? ["원티드 일치 반영"] : []),
    ...messageItems.map(({ label, content }) => `${label}: ${content}`),
  ].join(". ");

  const base = {
    displayShiftId,
    fontSize,
    nurseId,
    day,
    dataShiftId,
    dataDefaultShift,
    dataShiftGb,
    shiftBgColor,
    statusIndicator,
    isWantedMatch,
  };

  switch (cellUiMode) {
    case "weekly_locked":
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <ShiftCodeDomButton
              {...base}
              dataIsError={dataIsError}
              style={{ cursor: "not-allowed" }}
              onMouseEnter={onMouseEnterCell}
              onMouseLeave={onMouseLeaveCell}
              onClick={onWeeklyLockedClick}
            />
          </TooltipTrigger>
          <TooltipContent>
            <p>주휴 근무코드는 변경이 불가능합니다.</p>
          </TooltipContent>
        </Tooltip>
      );

    case "violation_messages":
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <ShiftCodeDomButton
              {...base}
              dataIsError={dataIsError}
              onMouseEnter={onMouseEnterCell}
              onMouseLeave={onMouseLeaveCell}
              onClick={onShiftCellClickEditOrFocus}
            />
          </TooltipTrigger>
          <TooltipContent className="bg-[#444444] text-white whitespace-nowrap">
            <div className="space-y-1">
              <p className="font-semibold text-sm mb-1">위반사항:</p>
              {displayedViolationMessages.map((msg, idx) => (
                <p key={idx} className="text-xs">
                  {msg}
                </p>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      );

    case "emergency_locked":
      return (
        <ShiftCodeDomButton
          {...base}
          dataIsError={dataIsError}
          style={{ cursor: "not-allowed" }}
          onMouseEnter={onMouseEnterCell}
          onMouseLeave={onMouseLeaveCell}
          onClick={onEmergencyLockedClick}
        />
      );

    case "assignment_locked":
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <ShiftCodeDomButton
              {...base}
              dataIsError={dataIsError}
              className="opacity-40"
              style={{ cursor: "not-allowed" }}
              onMouseEnter={onMouseEnterCell}
              onMouseLeave={onMouseLeaveCell}
              onClick={onShiftCellClickEditOrFocus}
            />
          </TooltipTrigger>
          <TooltipContent>
            <p>파견 근무는 수정이 불가합니다.</p>
          </TooltipContent>
        </Tooltip>
      );

    case "comment_popover":
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <ShiftCodeDomButton
              {...base}
              dataIsError={dataIsError}
              aria-label={messageAriaLabel}
              onMouseEnter={onMouseEnterCell}
              onMouseLeave={onMouseLeaveCell}
              onClick={onShiftCellClickEditOrFocus}
            />
          </TooltipTrigger>
          <TooltipContent
            className="z-50 w-max min-w-20 max-w-60 whitespace-normal border border-slate-200 bg-white px-3 py-2 text-slate-900 shadow-lg"
            side="top"
            sideOffset={7}
            align="start"
            arrowClassName="fill-white stroke-slate-200"
          >
            <div className="space-y-2">
              {messageItems.map((message, index) => (
                <div
                  key={message.label}
                  className={cn(index > 0 && "border-t border-slate-100 pt-2")}
                >
                  <p className="break-words text-sm font-medium leading-5 text-slate-900">
                    {message.content}
                  </p>
                  <p className="mt-0.5 text-[10px] font-medium text-slate-500">
                    {message.label}
                  </p>
                </div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      );

    case "wanted_mismatch":
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <ShiftCodeDomButton
              {...base}
              dataIsError={dataIsError}
              onMouseEnter={onMouseEnterCell}
              onMouseLeave={onMouseLeaveCell}
              onClick={onShiftCellClickEditOrFocus}
            />
          </TooltipTrigger>
          <WantedViolationTooltipContent
            displayShiftId={displayShiftId}
            isWantedMismatch
            bannedShiftIds={undefined}
            wantedShiftId={wantedShiftId}
          />
        </Tooltip>
      );

    case "wanted_match":
      return (
        <ShiftCodeDomButton
          {...base}
          dataIsError={dataIsError}
          aria-label="원티드 일치 반영"
          onMouseEnter={onMouseEnterCell}
          onMouseLeave={onMouseLeaveCell}
          onClick={onShiftCellClickEditOrFocus}
        />
      );

    case "banned_wanted_violation":
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <ShiftCodeDomButton
              {...base}
              dataIsError={dataIsError}
              aria-label={`금지 근무 위반: 현재 근무코드 ${displayShiftId}`}
              onMouseEnter={onMouseEnterCell}
              onMouseLeave={onMouseLeaveCell}
              onClick={onShiftCellClickEditOrFocus}
            />
          </TooltipTrigger>
          <WantedViolationTooltipContent
            displayShiftId={displayShiftId}
            isWantedMismatch={isWantedMismatch}
            bannedShiftIds={bannedShiftIds}
            wantedShiftId={wantedShiftId}
          />
        </Tooltip>
      );

    case "default":
      return (
        <ShiftCodeDomButton
          {...base}
          onMouseEnter={onMouseEnterCell}
          onMouseLeave={onMouseLeaveCell}
          onClick={onShiftCellClickEditOrFocus}
        />
      );
  }
}
